import type { BaseCampaign, TAdChannelKey } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';
import { useFlags } from '@feathr/extender/state';

export enum EChannelFilter {
  Overview = 'overview',
  Display = 'display',
  Meta = 'meta',
}

/**
 * Maps each channel filter tab to the corresponding backend stats channel key.
 * Overview returns undefined (use combined top-level stats).
 */
export const channelFilterToStatsKey: Record<EChannelFilter, TAdChannelKey | undefined> = {
  [EChannelFilter.Overview]: undefined,
  [EChannelFilter.Display]: 'tradedesk',
  [EChannelFilter.Meta]: 'meta',
};

interface IUseChannelFilterTabsReturn {
  shouldShowChannelTabs: boolean;
  availableChannels: EChannelFilter[];
}

/**
 * Determines whether channel filter tabs should be shown on the campaign report page.
 *
 * Channel tabs are shown when:
 * - metaV2 feature flag is enabled
 * - Campaign is an Omni (Pixl+) campaign
 * - Campaign has both Display (Tradedesk) and Meta (Facebook) integrations
 *
 * @param campaign - The campaign to check
 * @returns Object with shouldShowChannelTabs boolean and availableChannels array
 *
 * @example
 * const { shouldShowChannelTabs, availableChannels } = useChannelFilterTabs(campaign);
 */
export function useChannelFilterTabs(campaign: BaseCampaign): IUseChannelFilterTabsReturn {
  const { metaV2 = false } = useFlags();

  if (!metaV2) {
    return { shouldShowChannelTabs: false, availableChannels: [] };
  }

  if (!campaign.isOmniCampaign) {
    return { shouldShowChannelTabs: false, availableChannels: [] };
  }

  const integrations = campaign.get('integrations') ?? ([] as EIntegrationTypes[]);
  const hasDisplay = integrations.includes(EIntegrationTypes.Tradedesk);
  const hasMeta = integrations.includes(EIntegrationTypes.Meta);

  if (!hasDisplay || !hasMeta) {
    return { shouldShowChannelTabs: false, availableChannels: [] };
  }

  return {
    shouldShowChannelTabs: true,
    availableChannels: [EChannelFilter.Display, EChannelFilter.Meta],
  };
}
